import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  essential: boolean;
  performance: boolean;
  functional: boolean;
  advertising: boolean;
}

const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    performance: false,
    functional: false,
    advertising: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      performance: true,
      functional: true,
      advertising: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      performance: false,
      functional: false,
      advertising: false,
    };
    setPreferences(onlyEssential);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyEssential));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleTogglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return;
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl pointer-events-auto border border-gray-200">
        {!showSettings ? (
          <div className="p-6">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-cookie-line text-white text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Nous utilisons des cookies
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Ce site utilise des cookies pour améliorer votre expérience de navigation, analyser le trafic et personnaliser le contenu. En cliquant sur "Accepter tout", vous consentez à l'utilisation de tous les cookies.
                </p>
                <p className="text-gray-600 text-sm">
                  Мы используем cookies для улучшения вашего опыта, анализа трафика и персонализации контента.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleAcceptAll}
                className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
              >
                Accepter tout
              </button>
              <button
                onClick={handleRejectAll}
                className="bg-gray-200 text-gray-900 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium whitespace-nowrap"
              >
                Refuser
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="bg-white text-gray-900 px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
              >
                Personnaliser
              </button>
              <Link
                to="/cookies-policy"
                className="text-teal-600 hover:text-teal-700 px-6 py-3 rounded-lg font-medium whitespace-nowrap flex items-center"
              >
                En savoir plus
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Paramètres des cookies
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Cookies essentiels
                    </h4>
                    <p className="text-sm text-gray-600">
                      Nécessaires au fonctionnement du site. Ne peuvent pas être désactivés.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-teal-600 rounded-full flex items-center px-1">
                      <div className="w-4 h-4 bg-white rounded-full ml-auto"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Cookies de performance
                    </h4>
                    <p className="text-sm text-gray-600">
                      Collectent des informations sur l'utilisation du site pour améliorer ses performances.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleTogglePreference('performance')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.performance ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.performance ? 'ml-auto' : ''
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Cookies fonctionnels
                    </h4>
                    <p className="text-sm text-gray-600">
                      Permettent de mémoriser vos choix pour une expérience personnalisée.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleTogglePreference('functional')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.functional ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.functional ? 'ml-auto' : ''
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Cookies publicitaires
                    </h4>
                    <p className="text-sm text-gray-600">
                      Utilisés pour afficher des publicités pertinentes et mesurer leur efficacité.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => handleTogglePreference('advertising')}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.advertising ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.advertising ? 'ml-auto' : ''
                      }`}></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleSavePreferences}
                className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
              >
                Enregistrer les préférences
              </button>
              <button
                onClick={handleAcceptAll}
                className="bg-gray-200 text-gray-900 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium whitespace-nowrap"
              >
                Accepter tout
              </button>
              <Link
                to="/cookies-policy"
                className="text-teal-600 hover:text-teal-700 px-6 py-3 rounded-lg font-medium whitespace-nowrap flex items-center"
              >
                En savoir plus
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
